import { useState } from 'react';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { useModal } from '../contexts/ModalContext';

interface CaseStatusEvent {
  status: string;
  at: string;
}

interface CaseDetail {
  caseId?: string;
  id?: string;
  status?: string;
  riskTier?: string;
  priority?: string;
  chiefComplaint?: string;
  userId?: string;
  userName?: string;
  assignedSpecialistId?: string;
  specialistName?: string;
  serviceType?: string;
  selectedServiceType?: string;
  pricePaid?: number | null;
  paymentId?: string | null;
  slaResponseTime?: string;
  slaDeadline?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  needsManualReconciliation?: boolean;
  createdAt?: string;
  updatedAt?: string;
  statusHistory?: CaseStatusEvent[];
}

interface PaginatedCases {
  items: CaseDetail[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const CASE_STATUSES = [
  'NEW', 'IN_POOL', 'SPECIALIST_INTERESTED', 'SPECIALIST_ASSIGNED', 'MATCHING', 'ASSIGNED',
  'IN_REVIEW', 'IN_PROGRESS', 'DRAFT', 'NEEDS_USER_INFO', 'SIGNED', 'ESCALATED', 'CLOSED',
  'COMPLETED', 'SCHEDULED', 'NO_SHOW', 'SPECIALIST_NO_SHOW', 'TECHNICAL_ISSUE', 'CANCELLED',
  'NO_SPECIALISTS_AVAILABLE', 'USER_DECLINED_SPECIALIST', 'EXPIRED', 'EXPIRED_NO_SPECIALISTS',
  'EXPIRED_ALL_PASSED', 'PENDING_MANUAL_REVIEW', 'FAILED',
];

const SERVICE_TYPES = [
  'case_review', 'second_opinion', 'tele_counseling', 'telemedicine', 'home_visit',
  'clinic_visit', 'diagnostic_image_review', 'video_case_review', 'lab_exam_review',
  'lab_exam_intake', 'diagnostic_image_intake',
];

const statusTone = (status?: string) => {
  if (!status) return 'neutral' as const;
  if (['CLOSED', 'COMPLETED', 'SIGNED'].includes(status)) return 'good' as const;
  if (
    ['ESCALATED', 'NEEDS_USER_INFO', 'FAILED', 'CANCELLED', 'NO_SHOW', 'SPECIALIST_NO_SHOW', 'EXPIRED', 'EXPIRED_NO_SPECIALISTS', 'EXPIRED_ALL_PASSED', 'NO_SPECIALISTS_AVAILABLE', 'TECHNICAL_ISSUE'].includes(status)
  )
    return 'bad' as const;
  if (['PENDING_MANUAL_REVIEW', 'USER_DECLINED_SPECIALIST'].includes(status)) return 'warn' as const;
  return 'warn' as const;
};

const fmtDate = (value?: string) => (value ? new Date(value).toLocaleString() : '—');

const fmtMoney = (cents?: number | null) =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;

export function CaseManagementPage() {
  const { confirm } = useModal();

  // ─── List / filter / pagination ──────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [expandedCaseId, setExpandedCaseId] = useState<string | null>(null);

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  if (statusFilter) params.set('status', statusFilter);
  if (serviceTypeFilter) params.set('serviceType', serviceTypeFilter);
  if (createdFrom) params.set('createdFrom', createdFrom);
  if (createdTo) params.set('createdTo', createdTo);

  const { data, loading, error, reload } = useApiResource(
    () => apiGet<PaginatedCases>(`/cases?${params.toString()}`),
    [page, statusFilter, serviceTypeFilter, createdFrom, createdTo],
  );

  const toggleExpanded = (id: string) => setExpandedCaseId((prev) => (prev === id ? null : id));

  /** Contents of the expanded drawer; DataTable supplies the full-width row around it. */
  const renderHistoryPanel = (c: CaseDetail) => {
    const id = c.caseId || c.id || '';
    if (expandedCaseId !== id) return null;
    const history = c.statusHistory ?? [];
    return (
          <div className="form-card" style={{ maxWidth: 'none' }}>
            <label>Status history</label>
            {history.length === 0 ? (
              <p className="form-hint">No history recorded for this case yet.</p>
            ) : (
              <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {history.map((event, i) => (
                  <li key={i} style={{ marginBottom: '0.35rem' }}>
                    <StatusBadge label={event.status} tone={statusTone(event.status)} /> — {fmtDate(event.at)}
                  </li>
                ))}
              </ol>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 2rem', marginTop: '0.75rem' }}>
              <div><strong>Case ID:</strong> {id}</div>
              <div><strong>Patient:</strong> {c.userName || c.userId || '—'}</div>
              <div><strong>Specialist:</strong> {c.specialistName || c.assignedSpecialistId || '— unassigned —'}</div>
              <div><strong>Service type:</strong> {c.serviceType || c.selectedServiceType || '—'}</div>
              <div><strong>Risk tier:</strong> {c.riskTier || '—'}</div>
              <div><strong>Price paid:</strong> {fmtMoney(c.pricePaid)}</div>
              <div><strong>SLA response time:</strong> {c.slaResponseTime || '—'}</div>
              <div><strong>SLA deadline:</strong> {fmtDate(c.slaDeadline)}</div>
              <div><strong>Created:</strong> {fmtDate(c.createdAt)}</div>
              <div><strong>Last updated:</strong> {fmtDate(c.updatedAt)}</div>
              {c.cancellationReason && (
                <div><strong>Cancellation reason:</strong> {c.cancellationReason} ({fmtDate(c.cancelledAt)})</div>
              )}
              {c.needsManualReconciliation && (
                <div style={{ color: 'var(--danger-bold)' }}><strong>⚠ Needs manual reconciliation</strong></div>
              )}
            </div>
            {c.chiefComplaint && (
              <div style={{ marginTop: '0.5rem', maxWidth: 640 }}>
                <strong>Chief complaint:</strong> {c.chiefComplaint}
              </div>
            )}
          </div>
    );
  };

  // ─── Single-case lookup + assign (existing tool, kept as-is below the table) ──
  const [lookupId, setLookupId] = useState('');
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [specialistId, setSpecialistId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignMessage, setAssignMessage] = useState<string | null>(null);

  const runLookup = async () => {
    const id = lookupId.trim();
    if (!id) return;
    setLookupLoading(true);
    setLookupError(null);
    setAssignMessage(null);
    setCaseDetail(null);
    try {
      const found = await apiGet<CaseDetail>(`/cases/${encodeURIComponent(id)}`);
      setCaseDetail(found);
      setSpecialistId(found.assignedSpecialistId || '');
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : 'Case not found');
    } finally {
      setLookupLoading(false);
    }
  };

  const assign = async () => {
    if (!caseDetail) return;
    const id = caseDetail.caseId || caseDetail.id;
    const targetSpecialistId = specialistId.trim();
    if (!id || !targetSpecialistId) return;

    const verb = caseDetail.assignedSpecialistId ? 'Reassign' : 'Assign';
    const ok = await confirm({ title: `${verb} case`, message: `${verb} case ${id} to specialist ${targetSpecialistId}?` });
    if (!ok) return;

    setAssigning(true);
    setAssignError(null);
    setAssignMessage(null);
    try {
      await apiPatch(`/cases/${encodeURIComponent(id)}/assign`, { specialistId: targetSpecialistId });
      setAssignMessage(`Case ${id} assigned to ${targetSpecialistId}.`);
      void runLookup();
      reload();
    } catch (err) {
      setAssignError(err instanceof ApiError ? err.message : 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Case Management</h1>
          <p>Every case across all services — status, full lifecycle history, and every detail on record.</p>
        </div>
      </div>

      <div className="section-toolbar">
        <label htmlFor="status-filter">Status</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All</option>
          {CASE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <label htmlFor="service-type-filter">Service type</label>
        <select
          id="service-type-filter"
          value={serviceTypeFilter}
          onChange={(e) => { setServiceTypeFilter(e.target.value); setPage(1); }}
        >
          <option value="">All</option>
          {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <label htmlFor="created-from">Created from</label>
        <input
          id="created-from"
          type="date"
          value={createdFrom}
          onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }}
        />

        <label htmlFor="created-to">to</label>
        <input
          id="created-to"
          type="date"
          value={createdTo}
          onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }}
        />

        <button className="btn" onClick={reload}>Refresh</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <DataTable
        id="case-management"
        rows={data?.items}
        loading={loading}
        empty="No cases match this filter."
        noun="cases"
        getRowKey={(c) => c.caseId || c.id || ''}
        expandedRow={renderHistoryPanel}
        pagination={
          data
            ? {
                page: data.page,
                totalPages: data.totalPages,
                total: data.total,
                hasNext: data.hasNext,
                hasPrev: data.hasPrev,
                onPageChange: setPage,
                noun: 'cases',
              }
            : undefined
        }
        columns={[
          { key: 'case', header: 'Case', value: (c) => c.caseId || c.id || '' },
          {
            key: 'status',
            header: 'Status',
            value: (c) => c.status || 'UNKNOWN',
            filter: 'select',
            render: (c) => <StatusBadge label={c.status || 'UNKNOWN'} tone={statusTone(c.status)} />,
          },
          {
            key: 'serviceType',
            header: 'Service type',
            value: (c) => c.serviceType || c.selectedServiceType || '—',
            filter: 'select',
          },
          { key: 'patient', header: 'Patient', value: (c) => c.userName || c.userId || '—' },
          {
            key: 'specialist',
            header: 'Specialist',
            value: (c) => c.specialistName || c.assignedSpecialistId || '— unassigned —',
          },
          {
            key: 'price',
            header: 'Price paid',
            value: (c) => c.pricePaid ?? 0,
            render: (c) => fmtMoney(c.pricePaid),
            align: 'right',
          },
          {
            key: 'created',
            header: 'Created',
            value: (c) => c.createdAt ?? '',
            render: (c) => fmtDate(c.createdAt),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (c) => {
              const id = c.caseId || c.id || '';
              return (
                <button className="btn" onClick={() => toggleExpanded(id)}>
                  {expandedCaseId === id ? 'Hide history' : 'View history'}
                </button>
              );
            },
          },
        ]}
      />

      <div className="form-card" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <label htmlFor="caseLookupId">Assign/reassign a case by ID</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            id="caseLookupId"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="case-abc123"
            style={{ flex: 1 }}
          />
          <button type="button" className="btn btn-primary" disabled={lookupLoading} onClick={() => void runLookup()}>
            {lookupLoading ? 'Looking up…' : 'Look up'}
          </button>
        </div>
        {lookupError && <p className="auth-error">{lookupError}</p>}
      </div>

      {caseDetail && (
        <div className="form-card">
          <label htmlFor="specialistId">
            {caseDetail.assignedSpecialistId ? 'Reassign to specialist ID' : 'Assign to specialist ID'}
          </label>
          <input
            id="specialistId"
            value={specialistId}
            onChange={(e) => setSpecialistId(e.target.value)}
            placeholder="Specialist Firebase UID"
          />
          {assignError && <p className="auth-error" role="alert">{assignError}</p>}
          <button className="btn btn-primary" type="button" disabled={assigning || !specialistId.trim()} onClick={() => void assign()}>
            {assigning ? 'Saving…' : caseDetail.assignedSpecialistId ? 'Reassign' : 'Assign'}
          </button>
        </div>
      )}

      {assignMessage && <div className="result-card">{assignMessage}</div>}
    </div>
  );
}
